/**
 * Adding a column worked out from other columns.
 *
 * A fixed set of operations rather than a formula box. A formula language
 * means parsing, cell references, a dependency graph and a whole category of
 * errors that only appear on row four million — and none of it is what a lead
 * file needs. What it needs is joining a name, pulling a postcode area out of
 * an address, and making a phone number dialable, which is what this offers.
 *
 * The value is worked out as the file is read, so a new column over five
 * million rows costs nothing until the sheet is saved.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Group, Modal, MultiSelect, NumberInput, Select, Stack, Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useAddComputedColumn } from '../api/queries';

/** What each operation needs from the user, so the form asks for that and no more. */
const OPERATIONS = [
  {
    value: 'combine',
    label: 'Join columns together',
    detail: 'First name and last name into one column.',
    sources: 'many' as const,
    needs: 'separator' as const,
    defaultSeparator: ' ',
  },
  {
    value: 'domain',
    label: 'Email domain',
    detail: 'Everything after the @.',
    sources: 'one' as const,
    needs: 'nothing' as const,
  },
  {
    value: 'phone_uk',
    label: 'Tidy UK phone number',
    detail: '44…, +44…, 7… and 07… all become 07…, with spaces removed.',
    sources: 'one' as const,
    needs: 'nothing' as const,
  },
  {
    value: 'valid_email',
    label: 'Flag bad emails',
    detail: 'yes or no per row. Blank cells stay blank, since missing is not wrong.',
    sources: 'one' as const,
    needs: 'nothing' as const,
  },
  {
    value: 'valid_phone',
    label: 'Flag bad phone numbers',
    detail: 'yes or no per row, after tidying. Blank cells stay blank.',
    sources: 'one' as const,
    needs: 'nothing' as const,
  },
  {
    value: 'part',
    label: 'One part of a split value',
    detail: 'The second piece of an address split on commas.',
    sources: 'one' as const,
    needs: 'separator+position' as const,
    defaultSeparator: ',',
  },
  {
    value: 'before',
    label: 'Text before a separator',
    detail: 'Everything up to the first match.',
    sources: 'one' as const,
    needs: 'separator' as const,
    defaultSeparator: ',',
  },
  {
    value: 'after',
    label: 'Text after a separator',
    detail: 'Everything past the first match. Empty when there is no match.',
    sources: 'one' as const,
    needs: 'separator' as const,
    defaultSeparator: ',',
  },
  {
    value: 'left',
    label: 'First few characters',
    detail: 'The outward code from a postcode, say.',
    sources: 'one' as const,
    needs: 'count' as const,
  },
  {
    value: 'right',
    label: 'Last few characters',
    detail: 'The last digits of a reference.',
    sources: 'one' as const,
    needs: 'count' as const,
  },
];

export function SheetComputedModal({
  sheetId,
  columns,
  startFrom,
  baseVersion,
  opened,
  onClose,
}: {
  sheetId: string | null;
  columns: string[];
  /** The column whose menu this was opened from, pre-selected as the source. */
  startFrom: string | null;
  /** The version this was decided against, so a stale change is refused. */
  baseVersion: number;
  opened: boolean;
  onClose: () => void;
}) {
  const add = useAddComputedColumn(sheetId);
  const [kind, setKind] = useState('combine');
  const [name, setName] = useState('');
  const [sources, setSources] = useState<string[]>([]);
  const [separator, setSeparator] = useState(' ');
  const [amount, setAmount] = useState<number>(1);

  const op = OPERATIONS.find((o) => o.value === kind) ?? OPERATIONS[0]!;

  // Opening from a column's menu means that column is what you had in mind.
  useEffect(() => {
    if (!opened) return;
    setSources(startFrom ? [startFrom] : []);
    setName('');
  }, [opened, startFrom]);

  useEffect(() => {
    if (op.defaultSeparator !== undefined) setSeparator(op.defaultSeparator);
  }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsSeparator = op.needs === 'separator' || op.needs === 'separator+position';
  const needsAmount = op.needs === 'count' || op.needs === 'separator+position';
  const enoughSources = op.sources === 'many' ? sources.length >= 2 : sources.length === 1;

  function submit() {
    add.mutate(
      {
        name,
        kind,
        // Only the first matters for the single-source operations, and sending
        // more would silently ignore them.
        sources: op.sources === 'many' ? sources : sources.slice(0, 1),
        ...(needsSeparator ? { find: separator } : {}),
        ...(needsAmount ? { amount: String(amount) } : {}),
        baseVersion,
      },
      {
        onSuccess: () => {
          onClose();
          notifications.show({
            color: 'teal',
            title: `${name} added`,
            message: 'Worked out as the file is read. Apply to file to keep it.',
          });
        },
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Could not add that column',
            message: (e as Error).message,
          }),
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Add a worked-out column" size="md">
      <Stack>
        <Select
          label="What to work out"
          data={OPERATIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={kind}
          onChange={(v) => setKind(v ?? 'combine')}
          allowDeselect={false}
        />
        <Text size="xs" c="dimmed" mt={-8}>{op.detail}</Text>

        <MultiSelect
          label={op.sources === 'many' ? 'From these columns, in order' : 'From this column'}
          description={
            op.sources === 'many'
              ? 'Two or more. The order here is the order they are joined in.'
              : undefined
          }
          data={columns}
          value={sources}
          onChange={(v) => setSources(op.sources === 'many' ? v : v.slice(-1))}
          searchable
        />

        {needsSeparator && (
          <TextInput
            label="Separator"
            description={
              op.value === 'combine'
                ? 'Put between the values. A space is usual for names.'
                : 'The text to split on.'
            }
            value={separator}
            onChange={(e) => setSeparator(e.currentTarget.value)}
          />
        )}

        {needsAmount && (
          <NumberInput
            label={op.needs === 'count' ? 'How many characters' : 'Which piece'}
            description={op.needs === 'count' ? undefined : 'Counting from 1.'}
            min={1}
            value={amount}
            onChange={(v) => setAmount(Number(v) || 1)}
          />
        )}

        <TextInput
          label="Name the new column"
          placeholder={op.value === 'domain' ? 'e.g. EmailDomain' : 'e.g. FullName'}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim() && enoughSources) submit();
          }}
        />

        <Alert color="gray" variant="light">
          <Text size="sm">
            The column is worked out as the file is read, so it costs nothing until you
            apply your changes to the file. Until then it can be removed like any other.
          </Text>
        </Alert>

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button
            loading={add.isPending}
            disabled={!name.trim() || !enoughSources}
            onClick={submit}
          >
            Add the column
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
